import ReservationTimelineView, {
    Id,
    PPTimelineItem,
    ReservationTimelineViewProps,
} from '../components/ReservationTimelineView'
import {ReactCalendarGroupRendererProps} from 'react-calendar-timeline'
import moment from 'moment-timezone'
import React, {CSSProperties, ReactNode} from 'react'
import styled from 'styled-components'
import {DisplaySizeValue, FontSize} from '../../../styledComponents/Constants'
import {StyledLink} from '../../../styledComponents/adminPage/Links'
import {Optional} from '../../../app/TypeHelpers'

type ItemType = 'otherReservation' | 'outsideHours' | 'current' | 'loading'
type ErrorType = 'overlap' | 'illegal'

export interface AdminTimelineItem extends PPTimelineItem {
    type: ItemType
    error?: ErrorType
}

type excludeProps =
    'items'
    | 'areaWidth'
    | 'groupRenderer'
    | 'maxWidth'
    | 'dateHeaderProps'
    | 'dateHeaderPropsDay'
    | 'dateHeaderPropsHour'
    | 'initialVisibleStartDateTime'

interface Props
    extends Omit<Partial<ReservationTimelineViewProps>, excludeProps> {
    items: AdminTimelineItem[]
    areaWidth: number
    initialVisibleStartDateTime: moment.Moment
    limitStartDateTime: moment.Moment
    limitEndDateTime: moment.Moment
    onGroupClick?: (groupId: Id) => void
    hideGroup?: boolean
    activeDay: moment.Moment
}

const AdminReservationTimelineView: React.FC<Props> = props => {
    const {children, items, groups,
        initialVisibleStartDateTime, activeDay, limitStartDateTime, limitEndDateTime} = props

    return <TimeLineStyle>
        <ReservationTimelineView
            stackItems={false}
            areaWidth={props.areaWidth}
            maxWidth={{
                pc: DisplaySizeValue.AdminWidthMax + (props.hideGroup ? 245 : 0),
                sp: 0,
            }}
            activeDay={activeDay}
            itemHeightRatio={0.9}
            groups={groups || []}
            items={items.map(item => convertToPPTimeLineItem(item, item.error))}
            initialVisibleStartDateTime={initialVisibleStartDateTime}
            limitStartDateTime={limitStartDateTime}
            limitEndDateTime={limitEndDateTime}
            groupRenderer={(groupProps: ReactCalendarGroupRendererProps): ReactNode => {
                if (props.hideGroup) {
                    return null
                }
                return (
                    <FacilityNameLinkButton
                        onClick={
                            () => props.onGroupClick
                                ? props.onGroupClick(groupProps.group.id)
                                : undefined
                        }
                        title={`${groupProps.group.title}`}
                    >
                        {groupProps.group.title}
                    </FacilityNameLinkButton>
                )
            }}
            onTimelineClick={props.onTimelineClick}
            onVisibleRangeChanged={props.onVisibleRangeChanged}
            dateHeaderPropsDay={{
                unit: 'day', height: 30,
                labelFormat: (date: any) => moment(limitStartDateTime).format('M月D日(dd)')
            }}
            dateHeaderPropsHour={{
                unit: 'hour', height: 30,
                labelFormat: (date: any) => {
                    const d = Array.isArray(date) ? date[0] : date;
                    return moment(d.$d).tz('Asia/Tokyo').format('H')
                }
            }}
            sidebarProps={props.hideGroup ? {pcWidth: 0, spWidth: 0} : {pcWidth: 245, spWidth: 245}}
            children={children}
        />
    </TimeLineStyle>
}

AdminReservationTimelineView.displayName = 'AdminReservationTimelineView'
export default AdminReservationTimelineView

const getStyle = (type: ItemType, error: Optional<ErrorType>): { style: CSSProperties } => {
    switch (type) {
        case 'loading':
            return {
                style: {
                    color: '#707070',
                    border: 'none',
                    cursor: 'default!important',
                    backgroundColor: '#f8f8f8',
                },
            }
        case 'otherReservation':
            return {
                style: {
                    color: 'white',
                    border: 'none',
                    cursor: 'default!important',
                    backgroundColor: 'rgb(141, 141, 141)',
                },
            }
        case 'outsideHours':
            return {
                style: {
                    color: '#707070',
                    border: 'none',
                    cursor: 'default!important',
                    backgroundColor: 'rgb(225, 225, 225)',
                },
            }
        case 'current':
            return {
                style: {
                    border: 'none',
                    backgroundColor: error ? 'rgb(235, 116, 130)' : 'rgb(97, 116, 193)',
                    cursor: 'default!important',
                    zIndex: 80,
                },
            }
        default:
            throw new Error(`unknown type ${type}`)
    }
}

const convertToPPTimeLineItem = (item: AdminTimelineItem, error: Optional<ErrorType> = undefined): PPTimelineItem => {
    const {type, ...rest} = item
    return {
        ...rest,
        itemProps: {
            ...getStyle(type, error),
        },
    }
}

const TimeLineStyle = styled.div`
.react-calendar-timeline {
    font-size: ${FontSize.Small};
    border-top: 1px solid #d9d9d9;
    border-bottom: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-horizontal-lines .rct-hl-odd {
    background: rgba(0, 0, 0, 0);
}
.react-calendar-timeline .rct-dateHeader {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    border-bottom: none;
    cursor: default;
    background-color: #f3f8fc;
    border-left: 1px dashed #d9d9d9;
}
.react-calendar-timeline .rct-dateHeaderDay {
    display: flex;
    align-items: center;
    height: 100%;
    border-bottom: 1px solid #d9d9d9;
    cursor: default;
    background-color: #f3f8fc;
    border-left: 1px dashed #d9d9d9;
    justify-content: flex-start;
    padding: 0 10px 0 10px;
}

.react-calendar-timeline .rct-calendar-header {
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
}
.react-calendar-timeline .rct-vertical-lines .rct-vl.rct-vl-first {
    //border-left-width: 0;
}
.react-calendar-timeline .rct-header-root {
    background: #FFFFFF;
    border-bottom: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-header-root > div:first-child {
    background-color: rgba(0,0,0,0);
    border-right: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-header-root > div:first-child > div {
    border: none;
}
.react-calendar-timeline .rct-sidebar {
  border-right: 1px solid #d9d9d9;
}
.react-calendar-timeline .rct-sidebar .rct-sidebar-row {
    padding: 0 4px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    box-sizing: border-box;
    margin: 0;
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
    background: transparent;
}
.react-calendar-timeline .rct-sidebar .rct-sidebar-row:first-child {
    border-top: 1px solid transparent!important;
}
.react-calendar-timeline .rct-sidebar .rct-sidebar-row.rct-sidebar-row-odd {
    background: transparent;
}
.rct-outer {
  border-left: none;
  border-right: none;
}

.timeline-out-of-target {
  background: rgba(176, 176, 176, 0.4)!important;
}
.react-calendar-timeline .rct-scroll {
  border-top: none;
  background: white;
}
.react-calendar-timeline .rct-vertical-lines .rct-vl {
  position: absolute;
  z-index: 81!important;
  border-left: 1px dashed #d9d9d9;
}
.react-calendar-timeline .rct-horizontal-lines .rct-hl-even,
.react-calendar-timeline .rct-horizontal-lines .rct-hl-odd {
    border-top: 1px solid #d9d9d9;
    border-bottom: none;
    box-sizing: border-box;
}
.react-calendar-timeline .rct-horizontal-lines .rct-hl-even:first-child,
.react-calendar-timeline .rct-horizontal-lines .rct-hl-odd:first-child {
    border-top: none;
}
.react-calendar-timeline .rct-calendar-header {
    border: none;
}
.react-calendar-timeline .rct-vertical-lines .rct-vl.rct-day-6,
.react-calendar-timeline .rct-vertical-lines .rct-vl.rct-day-0 {
  background: none;
}
`

const FacilityNameLinkButton = styled(StyledLink)`
  font-size: 16px;
  text-align: left;
  height: 100%;
  width: 100%;
  white-space: normal;
  overflow: hidden;
`
